import type { AuthSessionResponse, Permission, SessionSummary } from '@jaf/contracts';
import type { QueryClient } from '@tanstack/react-query';
import { redirect } from 'react-router';
import { ApiError, authClient, sessionRefresher, sessionStore } from '@/shared/api';
import { paths } from '@/shared/lib/paths';
import { logoutAllDevices } from './api/account-api';

/**
 * Garante sessão antes de carregar dados de rota (compartilha o refresh single-flight).
 * Sem sessão, redireciona para o login preservando o caminho interno de retorno.
 */
export async function requireSession(request: Request): Promise<SessionSummary> {
  const current = sessionStore.getState();
  if (current.status === 'authenticated' && current.session !== null) return current.session;
  const outcome = await sessionRefresher.refresh();
  const { session } = sessionStore.getState();
  if (outcome === 'authenticated' && session !== null) return session;
  if (outcome === 'forbidden') throw redirect(paths.forbidden());
  const url = new URL(request.url);
  throw redirect(paths.login(`${url.pathname}${url.search}`));
}

/** Indica se a sessão concede a permissão em alguma empresa (controle visual; a API revalida). */
export function hasPermission(session: SessionSummary | null, permission: Permission): boolean {
  return session?.permissions.includes(permission) === true;
}

/** Sessão numa organização: a única que concede permissões (o admin geral pode não ter uma). */
export type OrganizationSession = SessionSummary & { organizationId: string };

/**
 * Loader de rota protegida: garante a sessão e a permissão em alguma empresa. Sem ela, lança
 * 403 (o `errorElement` mostra "Acesso negado") antes de pré-carregar dados.
 */
export async function requirePermission(
  request: Request,
  permission: Permission,
): Promise<OrganizationSession> {
  const session = await requireSession(request);
  const { organizationId } = session;
  if (organizationId === null || !hasPermission(session, permission)) {
    throw new ApiError({ kind: 'http', status: 403, title: 'Acesso negado' });
  }
  return { ...session, organizationId };
}

/**
 * Loader das rotas do admin geral (ADR-0021): garante a sessão de admin geral; sem ela, 403
 * ("Acesso negado") antes de pré-carregar dados.
 */
export async function requirePlatformAdmin(request: Request): Promise<SessionSummary> {
  const session = await requireSession(request);
  if (!session.platformAdmin) {
    throw new ApiError({ kind: 'http', status: 403, title: 'Acesso negado' });
  }
  return session;
}

/** Tela inicial da sessão: o admin geral sem organização começa pelas organizações. */
export function homePathFor(session: SessionSummary): string {
  return session.organizationId === null && session.platformAdmin
    ? paths.platformOrganizations()
    : paths.companies();
}

/** Encerra a sessão: revoga no backend, cancela requisições e limpa todos os caches. */
export async function endSession(queryClient: QueryClient): Promise<void> {
  sessionStore.getState().end('anonymous');
  await queryClient.cancelQueries();
  queryClient.clear();
  await authClient.logout().catch(() => undefined);
}

/** Guarda em memória a sessão recebida no login. */
export function startSession(response: AuthSessionResponse): void {
  sessionStore.getState().authenticate(response);
}

/**
 * Sai de todos os dispositivos: revoga as sessões no backend (precisa do access token, então
 * vem antes de limpar o estado local) e encerra a sessão deste navegador.
 */
export async function endAllSessions(queryClient: QueryClient): Promise<void> {
  await logoutAllDevices();
  sessionStore.getState().end('anonymous');
  await queryClient.cancelQueries();
  queryClient.clear();
}
